"use client";

import { ArrowLeft } from "lucide-react";
import { useRouter } from "next/navigation";
import type React from "react";
import { Button } from "@/components/tailwind/ui/button";

interface PageHeaderProps {
  /**
   * The main title of the page
   */
  title: string;

  /**
   * Optional description/subtitle text
   */
  description?: string;

  /**
   * Whether to show a back button (typically for secondary pages)
   */
  showBackButton?: boolean;

  /**
   * Optional icon to display next to the title
   */
  icon?: React.ReactNode;

  /**
   * Optional action buttons/elements to display on the right side
   */
  actions?: React.ReactNode;

  /**
   * Optional custom back action
   */
  onBack?: () => void;

  /**
   * Additional CSS classes for the container
   */
  className?: string;
}

/**
 * Standardized page header component for consistent styling across the app
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  showBackButton = false,
  icon,
  actions,
  onBack,
  className = "",
}) => {
  const router = useRouter();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      router.back();
    }
  };

  return (
    <div className={`mb-8 ${className}`}>
      {showBackButton && (
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {icon && <div className="text-gray-600 dark:text-gray-400">{icon}</div>}
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{title}</h1>
          </div>
          {description && <p className="text-gray-600 dark:text-gray-400 mt-2">{description}</p>}
        </div>

        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
    </div>
  );
};
